import moment from 'moment/moment';
import ERC20 from '../abi/ERC20.json';
import { ethers } from 'ethers';
import { getAddress } from './get-address';
import StakeABI from '../abi/StakeABI.json';

const timestampToTime = (timestamp) => {
    timestamp = Number(timestamp);
    var d = Math.floor(timestamp / (3600 * 24));
    var h = Math.floor((timestamp % (3600 * 24)) / 3600);
    var dDisplay = d > 0 ? d + (d === 1 ? ' Day ' : ' Days ') : '';
    var hDisplay = h > 0 ? h + (h === 1 ? ' Hour ' : ' Hours ') : '';
    return dDisplay || hDisplay || '0 Hours';
};

const formatTimestampToDate = (timestamp) => {
    const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year}`;
};

const weiToNumber = (value, decimals = 18) => parseFloat(ethers.utils.formatUnits(value, decimals));

function beautifyNumber(number, maxFractions) {
    if (typeof number !== "number" && isNaN(Number(number))) {
        throw new Error("Input must be a valid number");
    }
    return Number(number).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxFractions
    });
}

function convertSecondsToHours(seconds) {
    if (typeof seconds !== "number" || seconds < 0) {
        throw new Error("Please provide a valid number of seconds.");
    }
    return Math.round(parseFloat(seconds / 3600));
}

function convertSecondsToMinutes(seconds) {
    if (typeof seconds !== "number" || seconds < 0) {
        throw new Error("Please provide a valid number of seconds.");
    }
    return Math.round(parseFloat(seconds / 60));
}

function convertSecondsToDays(seconds) {
    if (typeof seconds !== "number" || seconds < 0) {
        throw new Error("Please provide a valid number of seconds.");
    }
    return Math.round(parseFloat(seconds / 86400));
}

function splitArrayByType(inputArray) {
    const rewardClaimHistory = [];
    const bonusClaimHistory = [];

    inputArray.forEach((record) => {
        if (record[0] === 0) {
            rewardClaimHistory.push(record);
        } else if (record[0] === 1) {
            bonusClaimHistory.push(record);
        }
    });
    return { rewardClaimHistory, bonusClaimHistory };
}


// Function to fetch all non-user-specific data from the smart contract
const fetchStakeData = async () => {
    let success = false;
    try {
        // const base_rpc = process.env.REACT_APP_BASE_TESTNET;
        const base_rpc = process.env.REACT_APP_BASE_MAINNET;
        const provider = new ethers.providers.JsonRpcProvider(base_rpc);
        const { chainId } = await provider.getNetwork();
        const addresses = getAddress(chainId);
        const allocatedAmount = "100,000,000";
        if (addresses) {
            const tokenContract = new ethers.Contract(addresses.hope_address, ERC20.abi, provider);
            const contract = new ethers.Contract(
                addresses.stake_address,
                StakeABI.abi,
                provider
            );

            const [
                rewardRatePerEpoch,
                rewardEpochDuration,
                stakeFee,
                minStakeAmount,
                referralRewardRate,
                maxBonusAmount,
                totalStakedAmount,
                totalClaimedAmount,
                totalStakers,
                lockPeriod,
            ] = await Promise.all([
                contract.rewardRatePerEpoch(),
                contract.rewardEpochDuration(),
                contract.stakeFee(),
                contract.minStakeAmount(),
                contract.referralRewardRate(),
                contract.maxBonusAmount(),
                contract.totalStakedAmount(),
                contract.totalClaimedAmount(),
                contract.totalStakers(),
                contract.lockPeriod(),
            ]);

            const balanceResponse = await tokenContract.balanceOf(addresses.stake_address);
            const formattedBalance = beautifyNumber(parseInt(weiToNumber(balanceResponse)), 0);
            const epochDuration = convertSecondsToHours(weiToNumber(rewardEpochDuration, 0));
            const epochReward = weiToNumber(rewardRatePerEpoch);
            const dailyEpochs = 24 / epochDuration;
            const weeklyEpocks = 168 / epochDuration;
            const apy = Math.round(dailyEpochs * epochReward * 365 * 100);
            const weeklyYield = parseFloat(weeklyEpocks * epochReward).toFixed(4);
            const dailyYield = parseFloat(dailyEpochs * epochReward).toFixed(4);

            return {
                success: true,
                apy,
                weeklyYield,
                dailyYield,
                hopeAllocated: allocatedAmount,
                hopeRemainingBalance: formattedBalance,
                rewardRatePerEpoch: epochReward,
                rewardEpochDuration: epochDuration,
                stakeFee: weiToNumber(stakeFee, 18),
                minStakeAmount: weiToNumber(minStakeAmount),
                referralRewardRate: parseInt(weiToNumber(referralRewardRate) * 100),
                maxBonusAmount: weiToNumber(maxBonusAmount),
                totalStakedAmount: beautifyNumber(weiToNumber(totalStakedAmount), 0),
                totalClaimedAmount: beautifyNumber(weiToNumber(totalClaimedAmount), 0),
                totalStakers: beautifyNumber(parseInt(totalStakers, 10), 0), // Stakers count is an integer
                lockPeriod: convertSecondsToMinutes(weiToNumber(lockPeriod, 0)), // Lock period in seconds
            };

        } else {
            return {
                success,
                message: 'Wrong network!',
            };
        }
    } catch (e) {
         return {
            success,
            message: e.message,
        };
    }
};

// Function to fetch user-specific data from the smart contract
const fetchUserData = async (userAddress) => {
    let success = false;
    try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const { chainId } = await provider.getNetwork();
        const addresses = getAddress(chainId);

        if (addresses) {
            const signer = provider.getSigner();
            const stakeContract = new ethers.Contract(
                addresses.stake_address,
                StakeABI.abi,
                signer
            );

            const tokenContract = new ethers.Contract(
                addresses.hope_address,
                ERC20.abi,
                signer
            );
            const _balance = await tokenContract.balanceOf(userAddress);
            const _allowance = await tokenContract.allowance(userAddress, addresses.stake_address);
            const balance = parseFloat(_balance.toString()) / 10 ** 18;
            const formattedAllowance = parseFloat(_allowance.toString()) / 10 ** 18;


            const userData = await stakeContract.users(userAddress);
            const pendingReward = await stakeContract.calculatePendingRewards(userAddress);
            const bonusHistory = await stakeContract.getBonusHistory(userAddress);
            const claimHistory = await stakeContract.getStakeHistory(userAddress);

            const { rewardClaimHistory, bonusClaimHistory } = splitArrayByType(claimHistory);
            const totalRewardClaimed = rewardClaimHistory.reduce((total, record) => total + (parseFloat(weiToNumber(record.claimedAmount.toString()), 18) || 0), 0);
            const totalBonusClaimed = bonusClaimHistory.reduce((total, record) => total + (parseFloat(weiToNumber(record.claimedAmount.toString()), 18) || 0), 0);

            const formattedRewardClaimHistory = [];
            for (let i of rewardClaimHistory) {
                formattedRewardClaimHistory.push({
                    claimTimestamp: formatTimestampToDate(i.claimTimestamp),
                    timestamp: formatTimestampToDate(i.timestamp),
                    claimedAmount: parseFloat(weiToNumber(i.claimedAmount.toString(), 18)).toFixed(2),
                    stakedAmount: parseFloat(weiToNumber(i.stakedAmount.toString(), 18)).toFixed(2)
                })
            }
            const formattedBonusClaimHistory = [];
            for (let i of bonusClaimHistory) {
                formattedBonusClaimHistory.push({
                    claimTimestamp: formatTimestampToDate(i.claimTimestamp),
                    claimedAmount: parseFloat(weiToNumber(i.claimedAmount.toString(), 18)).toFixed(2)
                })
            }

            const formattedBonusHistory = [];
            for (let i of bonusHistory) {
                formattedBonusHistory.push({
                    timestamp: formatTimestampToDate(i.timestamp),
                    bonusAmount: parseFloat(weiToNumber(i.bonusAmount.toString(), 18)).toFixed(2),
                    stakedAmount: parseFloat(weiToNumber(i.stakedAmount.toString(), 18)).toFixed(2)
                })
            }
        

            const _pendingReward = beautifyNumber(weiToNumber(pendingReward.toString(), 18), 4);
            const formattedStakedAmount = weiToNumber(userData.stakedAmount.toString(), 18);
            const formattedBonusDebt = weiToNumber(userData.bonusDebt.toString(), 18);
            const _stakedAmount = beautifyNumber(parseFloat(formattedStakedAmount.toString()), 2)
            const unstakeAmount = parseFloat(parseFloat(_stakedAmount) + parseFloat(formattedBonusDebt)).toFixed(2);
            const _bonusDebt = beautifyNumber(parseFloat(formattedBonusDebt.toString()), 2)
            const _referralLink = userData.referralLink.toString();
            const _claimCount = parseInt(userData.stakeCount.toString())
            const _bonusCount = parseInt(userData.bonusCount);
            const _claimTimestamp = userData.claimTimestamp.toString()
            const _stakeTimestamp = userData.stakeTimestamp.toString()


            return {
                success: true,
                hopeBalance: balance,
                pendingReward: beautifyNumber(_pendingReward, 2),
                unstakeAmount,
                bonusHistory: formattedBonusHistory,
                rewardClaimHistory: formattedRewardClaimHistory,
                bonusClaimHistory: formattedBonusClaimHistory,
                totalRewardClaimed: beautifyNumber(totalRewardClaimed, 2),
                totalBonusClaimed: beautifyNumber(totalBonusClaimed, 2),
                formattedHopeBalance: beautifyNumber(parseFloat(balance).toFixed(2)),
                stakeAllowance: formattedAllowance,
                stakedAmount: _stakedAmount,
                bonusCount: _bonusCount,
                bonusDebt: _bonusDebt,
                claimCount: _claimCount,
                claimTimestamp: _claimTimestamp,
                referralLink: _referralLink,
                stakeTimestamp: _stakeTimestamp

            };

        } else {
            return {
                success,
                message: 'Wrong network!',
            };
        }
    } catch (e) {
          return {
            success,
            message: e.message,
        };
    }
};

export { fetchStakeData, fetchUserData };
